import { Box, Flex, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr } from "@chakra-ui/react";
import type { ReactNode } from "react";

/**
 * The shared shape of every home panel table: no wrapping, so rows stay one line high and the panel
 * scrolls sideways instead of growing tall, and a title column that truncates rather than pushing the
 * other columns out of sight.
 *
 * The floor on the table's width is what makes the panel's horizontal scroll appear on a narrow
 * screen. Without it the title column would collapse toward nothing to keep the table fitting, and
 * the row would read as an ellipsis with a date next to it.
 */
export const PanelTable = ({ headers, children }: { headers: string[]; children: ReactNode }) => (
  <Table variant="simple" size="sm" whiteSpace="nowrap" minW="28rem">
    <Thead>
      <Tr>
        {headers.map((header) => (
          <Th key={header}>{header}</Th>
        ))}
      </Tr>
    </Thead>
    <Tbody>{children}</Tbody>
  </Table>
);

export const PanelRow = ({ onClick, children }: { onClick: () => void; children: ReactNode }) => (
  <Tr cursor="pointer" _hover={{ bg: "smPrimarySofter" }} onClick={onClick}>
    {children}
  </Tr>
);

/**
 * `maxW=0` with `w=100%` is what lets a cell in an auto-layout table shrink below its content width,
 * which is what makes the ellipsis appear at all. The column still takes whatever room the fixed-width
 * columns leave over.
 */
export const TitleCell = ({
  title,
  subtitle,
  subtitleColor,
  icon,
  isUnread,
  clampLines,
}: {
  title: ReactNode;
  subtitle?: string;
  subtitleColor?: string;
  icon?: ReactNode;
  isUnread?: boolean;
  /**
   * For titles that are whole sentences rather than names. A single-line ellipsis cannot cut a
   * composed description — `text-overflow` only reaches the element's own inline content, not the
   * markup a rendered description brings with it — so those wrap and clamp to this many lines.
   */
  clampLines?: number;
}) => (
  <Td maxW="0" w="100%" whiteSpace={clampLines ? "normal" : undefined}>
    <Flex align="center" gap="3" minW="0">
      {/*
        Unread used to be a heavier weight, which made rows in the same table disagree about how bold
        text should be. The dot says the same thing without touching the type — and matches how the
        notification list marks unread. Kept in the layout when read so rows stay aligned.
      */}
      {isUnread !== undefined && (
        <Box
          w="6px"
          h="6px"
          rounded="full"
          flexShrink={0}
          bg="smPrimary"
          visibility={isUnread ? "visible" : "hidden"}
          data-testid={isUnread ? "panel-unread-dot" : undefined}
        />
      )}
      {icon}
      <Box minW="0" flex="1">
        {clampLines ? (
          <Box
            overflow="hidden"
            color="smMuted"
            sx={{ display: "-webkit-box", WebkitLineClamp: clampLines, WebkitBoxOrient: "vertical" }}
          >
            {title}
          </Box>
        ) : (
          <Tooltip label={typeof title === "string" ? title : undefined} openDelay={600}>
            <Text isTruncated color="smMuted">
              {title}
            </Text>
          </Tooltip>
        )}
        {subtitle && (
          <Text isTruncated fontSize="xs" color={subtitleColor ?? "smMuted"}>
            {subtitle}
          </Text>
        )}
      </Box>
    </Flex>
  </Td>
);

export const MutedCell = ({ children }: { children: ReactNode }) => (
  <Td>
    <Text fontSize="sm" color="smMuted">
      {children}
    </Text>
  </Td>
);
